import React, { useEffect, useMemo, useState } from "react";
import { formatMoney, getNumber } from "../../../features/utils/formatMoney";
import { getCurrencySymbol } from "../../../features/utils/countires";
import CommonCollapsible from "../../common/CommonCollapsible";
import { clone } from "../../../features/utils/objClone";

const FIELD_LABELS = ["Change Fee", "Fare Difference", "Penalty"];

const defaultMarkup = {
  variant: "Markup",
  type: "Fixed",
  amount: "",
};

/** Markup applies to fare difference only; change fee and penalty stay at airline cost. */
const defaultMarkupTargets = {
  "Change Fee": false,
  "Fare Difference": true,
  Penalty: false,
};

const inputClass =
  "w-full h-8 px-2.5 text-sm border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] placeholder:text-[#A4B1C1] focus:outline-none focus:border-[#94A3B8]";

const selectClass =
  "h-8 px-2 text-xs border border-[#DBDBDB] rounded-lg bg-white text-[#0F172A] focus:outline-none focus:border-[#94A3B8]";

function applyAdjustment(base, markup) {
  const baseN = Math.max(0, Number(base) || 0);
  const raw = Number(markup?.amount);
  if (!Number.isFinite(raw) || raw <= 0) return baseN;

  const delta = markup?.type === "Percentage" ? baseN * (raw / 100) : raw;

  if (markup?.variant === "Markdown") {
    return Math.max(0, baseN - delta);
  }
  return baseN + delta;
}

function profitOn(base, adjusted) {
  return Number(adjusted || 0) - Number(base || 0);
}

function fieldKey(name) {
  if (name === "Penality") return "Penalty";
  return name;
}

/** Display-only label — payload / breakDown.name stays canonical. */
function displayFeeName(name, isDetailsChange) {
  // Details quotes store the amount on Fare Difference (change fee = 0).
  if (isDetailsChange && name === "Fare Difference") return "Correction Fee";
  if (isDetailsChange && name === "Change Fee") return "Correction Fee";
  return name;
}

export default function PassengerBreakdownCard({
  normalized,
  serviceFee,
  passengerCount,
  item,
  travellerLabel,
  travelerType,
  handleSaveBreakdown,
  isDetailsChange = false,
}) {
  const [open, setOpen] = useState(false);
  const [localBreakdown, setLocalBreakdown] = useState([]);
  const [markup, setMarkup] = useState(defaultMarkup);

  useEffect(() => {
    setLocalBreakdown(clone(item?.breakDown || []));
    setMarkup({
      variant: item?.markup?.variant || "Markup",
      type: item?.markup?.type || "Fixed",
      amount:
        item?.markup?.amount != null && item?.markup?.amount !== ""
          ? String(item.markup.amount)
          : "",
    });
    // eslint-disable-next-line
  }, [open, item?.travelerType]);

  const globalBreakdown = useMemo(() => item?.breakDown || [], [item]);

  const hasGlobalQuoteValue = useMemo(() => {
    return (globalBreakdown || []).some((x) => Number(x?.amount || 0) > 0);
  }, [globalBreakdown]);

  const adjustedBreakdown = useMemo(() => {
    return (localBreakdown || []).map((row) => {
      const key = fieldKey(row?.name);
      const applies = Boolean(defaultMarkupTargets[key]);
      return {
        ...row,
        adjusted: applies
          ? applyAdjustment(row?.amount, markup)
          : Math.max(0, Number(row?.amount) || 0),
        markupApplies: applies,
      };
    });
  }, [localBreakdown, markup]);

  const isDirty = useMemo(() => {
    const g = globalBreakdown || [];
    const l = localBreakdown || [];
    if (g.length !== l.length) return true;
    for (let i = 0; i < g.length; i++) {
      if (Number(g[i]?.amount || 0) !== Number(l[i]?.amount || 0)) return true;
    }
    const gm = item?.markup || defaultMarkup;
    if ((gm.variant || "Markup") !== (markup.variant || "Markup")) return true;
    if ((gm.type || "Fixed") !== (markup.type || "Fixed")) return true;
    if (String(gm.amount || "") !== String(markup.amount || "")) return true;

    return false;
  }, [globalBreakdown, localBreakdown, markup, item?.markup]);

  const icon = useMemo(() => {
    switch (travelerType) {
      case "CHD":
      case "CNN":
        return "./flightChild.svg";
      case "INF":
      case "INS":
        return "./flightInfant.svg";
      default:
        return "./flightAdult.svg";
    }
  }, [travelerType]);

  const localChargesPerPax = useMemo(() => {
    return adjustedBreakdown.reduce(
      (acc, cur) => acc + Number(cur?.adjusted || 0),
      0,
    );
  }, [adjustedBreakdown]);

  const platformProfitPerPax = useMemo(() => {
    return adjustedBreakdown.reduce(
      (acc, cur) => acc + profitOn(cur?.amount, cur?.adjusted),
      0,
    );
  }, [adjustedBreakdown]);

  const totalChargePerPax = useMemo(() => {
    return Number(localChargesPerPax || 0) + Number(serviceFee || 0);
  }, [localChargesPerPax, serviceFee]);

  const totalChargeForType = useMemo(() => {
    return totalChargePerPax * Number(passengerCount || 0);
  }, [totalChargePerPax, passengerCount]);

  const onChangeLocal = (i, v) => {
    const next = clone(localBreakdown || []);
    if (!next?.[i]) return;
    next[i].amount = Math.max(0, Number(getNumber(v) || 0));
    setLocalBreakdown(next);
  };

  const onMarkupChange = (patch) => {
    setMarkup((prev) => {
      const next = { ...prev, ...patch };
      if (
        next.variant === "Markdown" &&
        next.type === "Percentage" &&
        Number(next.amount) > 100
      ) {
        next.amount = "100";
      }
      return next;
    });
  };

  const onSave = () => {
    if (!isDirty) {
      setOpen(false);
      return;
    }
    handleSaveBreakdown?.(
      item?.travelerType,
      localBreakdown,
      {
        ...markup,
        amount: markup.amount === "" ? 0 : Number(markup.amount),
      },
      defaultMarkupTargets,
    );
    setOpen(false);
  };

  return (
    <div className="rounded-xl bg-white border border-[#E8EEF5] flex flex-col">
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer"
        onClick={() => setOpen((s) => !s)}
      >
        <div className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
          <img
            src={icon}
            alt=""
            className={
              "w-5 h-5 object-contain " +
              (icon === "./flightChild.svg" ? "mb-0.5" : "")
            }
          />
          <span>{travellerLabel?.[normalized] || normalized}</span>
          <span className="text-xs text-[#6D7C94] font-normal">
            ×{passengerCount}
          </span>
        </div>

        {!hasGlobalQuoteValue ? (
          <button
            type="button"
            className="h-7 rounded-lg border border-[#CBD5E1] text-[#0F172A] px-2.5 text-xs font-semibold bg-white hover:bg-[#F8FAFC]"
          >
            Add Quote
          </button>
        ) : (
          <div className="text-sm font-semibold text-[#0F172A]">
            {formatMoney(totalChargeForType)}
          </div>
        )}
      </div>

      <CommonCollapsible open={open}>
        <div className="border-t border-[#E8EEF5] px-3 pb-2.5 pt-1">
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_108px_96px] gap-2 pb-0.5">
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Fee
            </span>
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold text-right">
              Airline
            </span>
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold text-right">
              Adjusted
            </span>
          </div>

          <div className="divide-y divide-[#F1F5F9]">
            {adjustedBreakdown.map((obj, i) => {
              // Details quotes put the amount on fare difference — never show
              // a separate Change Fee row (always sent as 0).
              if (isDetailsChange && fieldKey(obj?.name) === "Change Fee")
                return null;
              const profit = profitOn(obj?.amount, obj?.adjusted);
              return (
                <div
                  key={obj?.name || i}
                  className="grid grid-cols-[minmax(0,1fr)_108px_96px] gap-2 items-center py-1.5"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0F172A] truncate leading-tight">
                      {displayFeeName(obj?.name, isDetailsChange)}
                    </div>
                    <div className="text-[10px] text-[#6D7C94]">per PAX</div>
                  </div>

                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${inputClass} text-right`}
                    placeholder="0"
                    value={
                      obj?.amount
                        ? String(Number(obj.amount).toLocaleString())
                        : ""
                    }
                    onChange={(e) => onChangeLocal(i, e.target.value)}
                  />

                  <div className="text-right leading-tight">
                    <div className="text-sm font-semibold text-[#0F172A]">
                      {formatMoney(obj?.adjusted || 0)}
                    </div>
                    {!obj?.markupApplies ? (
                      <div className="text-[10px] font-medium text-[#6D7C94]">
                        At cost
                      </div>
                    ) : profit !== 0 ? (
                      <div
                        className={`text-[10px] font-medium ${
                          profit > 0 ? "text-emerald-700" : "text-amber-700"
                        }`}
                      >
                        {profit > 0 ? "+" : ""}
                        {formatMoney(profit)}
                      </div>
                    ) : (
                      <div className="text-[10px] font-medium text-[#6D7C94]">
                        Marked up
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-1.5 rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-2 py-1.5">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div>
                <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                  Markup / Markdown
                </span>
                <div className="text-[10px] text-[#6D7C94]">
                  {isDetailsChange
                    ? "Applies to correction fee only · penalty at cost"
                    : "Applies to fare difference only · change fee & penalty at cost"}
                </div>
              </div>
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                  platformProfitPerPax > 0
                    ? "bg-emerald-100 text-emerald-800"
                    : platformProfitPerPax < 0
                      ? "bg-amber-100 text-amber-800"
                      : "bg-white text-[#6D7C94] border border-[#E8EEF5]"
                }`}
              >
                {platformProfitPerPax > 0
                  ? `+${formatMoney(platformProfitPerPax)}`
                  : platformProfitPerPax < 0
                    ? `−${formatMoney(Math.abs(platformProfitPerPax))}`
                    : "—"}
              </span>
            </div>

            <div className="flex gap-1.5">
              <select
                className={selectClass}
                value={markup.variant}
                onChange={(e) =>
                  onMarkupChange({ variant: e.target.value, amount: "" })
                }
              >
                <option value="Markup">Markup</option>
                <option value="Markdown">Markdown</option>
              </select>
              <select
                className={selectClass}
                value={markup.type}
                onChange={(e) =>
                  onMarkupChange({ type: e.target.value, amount: "" })
                }
              >
                <option value="Fixed">Fixed</option>
                <option value="Percentage">%</option>
              </select>
              <input
                type="text"
                inputMode="decimal"
                className={`${inputClass} flex-1`}
                placeholder={
                  markup.type === "Fixed" ? getCurrencySymbol("NGN") : "%"
                }
                value={markup.amount}
                onChange={(e) => {
                  const digitsOnly = String(e.target.value || "").replace(
                    /[^\d.]/g,
                    "",
                  );
                  onMarkupChange({ amount: digitsOnly });
                }}
              />
            </div>
          </div>

          <div className="mt-1.5 space-y-0.5 text-sm">
            <div className="flex items-center justify-between text-[#92400E] text-xs bg-[#FFFBEB] border border-[#FEF3C7] rounded-md px-2 py-1.5">
              <span className="font-medium">Intraverse service fee</span>
              <span className="font-semibold">
                {formatMoney(
                  Number(serviceFee || 0) * Number(passengerCount || 1),
                )}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-[#E8EEF5] pt-1 font-semibold text-[#0F172A] text-sm">
              <span>
                Total{" "}
                <span className="text-xs font-normal text-[#6D7C94]">
                  ×{passengerCount} · incl. service
                </span>
              </span>
              <span>{formatMoney(totalChargeForType)}</span>
            </div>
          </div>

          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty}
              className={`h-7 rounded-lg px-3 text-xs font-semibold transition
                ${
                  isDirty
                    ? "bg-primary1 text-white hover:opacity-90"
                    : "bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed"
                }`}
            >
              Save
            </button>
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
}

export { FIELD_LABELS, applyAdjustment, defaultMarkupTargets };
